window.o_global = window.o_global || {};
window.o_util = window.o_util || {};

/**
 * Generic helper module to perform an various asynchronous HTTP request using XMLHttpRequest.
 * o_util.ajax provides a dual-type API that can be used with Promises or callbacks.
 *
 * The return value either if used with promises or callbacks is a generic XMLHttpRequest object.
 * (https://developer.mozilla.org/de/docs/Web/API/XMLHttpRequest)
 *
 * @param {Object} options                      Options object. See parameters below.
 * @param {String} options.url                  Request URL.
 * @param {String} options.method               Request method ("GET", "POST", ...)
 * @param {String|Object} [options.data]        Request payload data. (Optional)
 * @param {String} [options.contentType]        Request header "content-type". (Optional)
 *                                              Default: "application/x-www-form-urlencoded; charset=UTF-8" POST requests.
 * @param {Number} [options.timeout]            Request timeout. Abort request after X ms. (Optional)
 * @param {Object} [options.headers]            Request custom headers. (Optional)
 * @param {Boolean} [options.withCredentials]   Request withCredentials property. (Optional)
 * @param {Function} [callback]                 The callback function will be executed after the request is completed.
 *                                              It gets the XMLHttpRequest object as the first argument.
 *                                              Only necessary if API is used with good ol' callbacks instead of Promises.
 *
 * @example
 * // Promise usage. XHR object will be first argument.
 * o_util.ajax({
 *     "url": "/url",
 *     "method": "GET",
 *     "headers": {
 *          "foo": "bar"
 *     }
 * }).then(function(xhr) {
 *      console.log("Status:", xhr.status);
 *      console.log("Response:", xhr.response);
 * });
 *
 * // Callback usage returns the XMLHttpRequest object.
 * var xhr = o_util.ajax({
 *     "url": "/url",
 *     "method": "GET",
 *     "headers": {
 *          "foo": "bar"
 *     }
 * }, function(xhr) {
 *     console.log("Status:", xhr.status);
 *     console.log("Response:", xhr.response);
 * });
 *
 * @return {Promise|XMLHttpRequest}     Returns an XMLHttpRequest object if called with callback. Otherwise it
 *                                      returns a Promise with the XMLHttpRequest object as first argument.
 */
o_util.ajax = function(options, callback) {
    "use strict";

    var optionsError = null;

    /**
     * Internal wrapper function to perform an asynchronous HTTP request.
     *
     * @private
     *
     * @param {Object} options          See parameter list for o_util.ajax().
     * @param {Function} callback       Will be called if request.readyState equals request.DONE (4).
     *
     * @return {XMLHttpRequest} XMLHttpRequest
     */
    function _sendRequest(options, callback) {
        var request = new XMLHttpRequest(),
            abortTimeout;

        request.open(options.method.toUpperCase(), options.url, true);
        o_util.ajax.active += 1;

        if (!!options.responseType) {
            // Set request.responseType if supported by the browser.
            if (typeof request.responseType === "string") {
                // Some browsers throw when setting 'responseType' to an unsupported value.
                try {
                    request.responseType = options.responseType;
                } catch (error) {
                }
            }
        }

        if (!!options.timeout) {
            // Set request.timeout if supported by the browser or start setTimeout.
            if (typeof request.timeout === "number") {
                request.timeout = options.timeout;
            } else {
                abortTimeout = setTimeout(request.abort, options.timeout);
            }
        }

        if (!!options.contentType) {
            // Set Content-Type if present in request options.
            request.setRequestHeader("Content-Type", options.contentType);
        }

        if (!!options.headers) {
            for (var header in options.headers) {
                if (options.headers.hasOwnProperty(header) && typeof options.headers[header] === "string") {
                    // Set Content-Type if present in request options.
                    request.setRequestHeader(header, options.headers[header]);
                }
            }
        }

        // Set withCredentials property of AJAX call if present in request options.
        if (!!options && typeof options.withCredentials === "boolean") {
            request.withCredentials = options.withCredentials;
        }

        // Enable ajax request detection with X-Requested-With header
        request.setRequestHeader("X-Requested-With", "XMLHttpRequest");

        request.addEventListener("abort", function() {
            o_util.ajax.active -= 1;
        });

        request.addEventListener("error", function() {
            o_util.ajax.active -= 1;
        });

        request.onreadystatechange = function() {
            if (request.readyState === XMLHttpRequest.DONE) {
                // Simulate request.timeout if not supported by the browser.
                if (!!abortTimeout) {
                    clearTimeout(abortTimeout);
                }

                o_util.ajax.active -= 1;

                // Check if request.responseType 'json' is not supported by the browser.
                if (!!options.responseType && options.responseType === "json" && !request.responseType) {
                    // Try to convert to JSON.
                    // Necessary due to lagging XMLHttpRequest 2 support in several browsers which supports responseType 'json'.
                    // @see http://caniuse.com/#feat=xhr2
                    if (!!request.responseText) {
                        // Set request.response to JSON object instead of a string.
                        request.responseJSON = JSON.parse(request.responseText);
                    }
                } else {
                    request.responseJSON = request.response;
                }

                // Call callback function.
                // Can be Promise.resolve or callback function from arguments.
                callback(request);
            }
        };

        // Send it with data if present.
        request.send(!!options.data ? options.data : null);

        // Return the request for callback mode.
        return request;
    }

    /**
     * Validates options for an AJAX request.
     *
     * @private
     *
     * @param {Object} options  The options object.
     *
     * @return {null|Error} Returns an error object in case of an error or null if everything was ok.
     */
    function _validateOptions(options) {
        if (!options) {
            return new Error("Parameter 'options' is missing.");
        }

        if (!options.method) {
            return new Error("Parameter 'options.method' is missing.");
        }

        if (!options.url) {
            return new Error("Parameter 'options.url' is missing.");
        }

        if (options.headers && !o_util.core.isPlainObject(options.headers)) {
            return new Error("Parameter 'options.headers' must be a key/value object if set");
        }

        return null;
    }

    optionsError = _validateOptions(options);

    if (typeof callback === "function") {
        // Return XHR if callback is a function or throw errors directly.
        if (optionsError instanceof Error) {
            throw optionsError;
        }

        return _sendRequest(options, callback);
    } else {
        // Return Promise if callback is not a function and reject errors.
        return new Promise(function(resolve, reject) {
            if (optionsError instanceof Error) {
                reject(optionsError);
            } else {
                return _sendRequest(options, resolve);
            }
        });
    }
};

// Extend o_util.ajax to make o_util.ajax() and o_util.ajax.get() possible.
o_util.ajax = o_util.core.extend(o_util.ajax, (function() {
    "use strict";

    /**
     * A counter of total current active XHR objects
     */
    var active = 0;

    /**
     * Perform an asynchronous HTTP GET request.
     *
     * @param {String} url                      Request URL.
     * @param {Function} [callback]             The callback function will be executed after the request is completed.
     *                                          It gets the XMLHttpRequest object as the first argument.
     * @example
     * // Promise.
     * o_util.ajax.get("/url").then(function(xhr) {
     *     console.log("status:", xhr.status);
     *     console.log("responseText:", xhr.responseText);
     * });
     *
     * // Callback.
     * o_util.ajax.get("/url", function(xhr) {
     *     console.log("status:", xhr.status);
     *     console.log("responseText:", xhr.responseText);
     * });
     *
     * @return {Promise|XMLHttpRequest}     Returns an XMLHttpRequest object if called with callback.
     *                                      (https://developer.mozilla.org/de/docs/Web/API/XMLHttpRequest)
     *                                      Otherwise it returns a Promise with the XMLHttpRequest object as first argument.
     */
    function get(url, callback) {
        return o_util.ajax({
            "method": "GET",
            "url": url
        }, callback);
    }

    /**
     * Perform an asynchronous HTTP GET request and retrieve a JSON object.
     * IMPORTANT: Use xhr.responseJSON to retrieve response as JSON object across all browsers!
     *
     * @param {String} url                      Request URL.
     * @param {Function} [callback]             The callback function will be executed after the request is completed.
     *                                          It gets the XMLHttpRequest object as the first argument.
     * @example
     * // Promise.
     * o_util.ajax.getJSON("/url").then(function(xhr) {
     *     console.log("status:", xhr.status);
     *     console.log("responseJSON:", xhr.responseJSON);
     * });
     *
     * // Callback.
     * o_util.ajax.getJSON("/url", function(xhr) {
     *     console.log("status:", xhr.status);
     *     console.log("responseJSON:", xhr.responseJSON);
     * });
     *
     * @return {Promise|XMLHttpRequest}     Returns an XMLHttpRequest object if called with callback.
     *                                      (https://developer.mozilla.org/de/docs/Web/API/XMLHttpRequest)
     *                                      Otherwise it returns a Promise with the XMLHttpRequest object as first argument.
     */
    function getJSON(url, callback) {
        return o_util.ajax({
            "method": "GET",
            "url": url,
            "responseType": "json"
        }, callback);
    }

    /**
     * Get a random string, composed by the current timestamp plus a 3 digit random number
     *
     * Used for cache busting requests.
     *
     * @private
     *
     * @return {String} The current time plus a 3 character random string
     */
    function _getCustomTimeStamp() {
        return Date.now().toString() + (Math.floor(Math.random() * 999)).toString();
    }

    /**
     * Perform an asynchronous HTTP GET request to retrieve a JavaScript-Source.
     * The loaded script can be execute immediately.
     *
     * @param {String} url                              Request URL.
     * @param {Boolean|Object} [options]                Original value = executeImmediately {Boolean}: Execute the loaded script immediately. Default: true
     *                                                  Extended value = options {Object}
     * @param {Boolean} [options.executeImmediately]    Execute the loaded script immediately. Default: true
     * @param {Boolean} [options.cacheBusting]          Set Timestamp to url to avoid caching. Default: true
     * @param {Function} [callback]                     The callback function will be executed after the request is completed.
     *                                                  It gets the XMLHttpRequest object as the first argument.
     *
     * @example
     * // Load and execute script.js.
     * o_util.ajax.getScript("/script.js");
     *
     * // Load script.js, but don't execute it. Use promise.
     * o_util.ajax.getScript("/script.js", false).then(function(xhr) {
     *     console.log("status:", xhr.status);
     *     console.log("script:", xhr.responseText);
     * });
     *
     * // Load script.js, but don't execute it. Callback version.
     * o_util.ajax.getScript("/script.js", false, function(xhr) {
     *     console.log("status:", xhr.status);
     *     console.log("script:", xhr.responseText);
     * });
     *
     * // Load script.js, but don't execute it and avoid cache-busting
     * o_util.ajax.getScript("/script.js", {"executeImmediately": false, "cacheBusting": false});
     *
     * @return {Promise|XMLHttpRequest}     Returns an XMLHttpRequest object if called with callback.
     *                                      (https://developer.mozilla.org/de/docs/Web/API/XMLHttpRequest)
     *                                      Otherwise it returns a Promise with the XMLHttpRequest object as first argument.
     */
    function getScript(url, options, callback) {
        var hasParams = (url.indexOf("?") !== -1),
            executeImmediately = true,
            cacheBusting = true;

        if (typeof options === "boolean") {
            executeImmediately = options;
        } else if (typeof options === "object") {
            if (typeof options.executeImmediately !== "undefined") {
                executeImmediately = options.executeImmediately;
            }

            if (typeof options.cacheBusting !== "undefined") {
                cacheBusting = options.cacheBusting;
            }
        }

        // Add a custom timestamp to prevent request caching.
        if (cacheBusting) {
            url += (hasParams) ? "&_=" + _getCustomTimeStamp() : "?_=" + _getCustomTimeStamp();
        }

        /**
         * Internal call used by getScript method to inject loaded javascript and execute it.
         *
         * @private
         *
         * @param {XMLHttpRequest} xhr      The native xhr object.
         *
         * @return {XMLHttpRequest} The native xhr object that contains the script source.
         */
        function _internalCallback(xhr) {
            var script;
            // Valid script can only be contained within `OK` or `Not Modified` status
            if ((xhr.status === 200 || xhr.status === 304) && executeImmediately !== false) {
                script = document.createElement("script");
                script.type = "text/javascript";
                script.text = xhr.responseText;

                o_util.hardcore.evalScript(script);
            }

            if (typeof callback === "function") {
                callback(xhr);
            }

            return xhr;
        }

        if (typeof callback === "function") {
            return o_util.ajax({
                "method": "GET",
                "url": url
            }, _internalCallback);
        } else {
            return o_util.ajax({
                "method": "GET",
                "url": url
            }).then(_internalCallback);
        }
    }

    /**
     * Perform an asynchronous HTTP POST request.
     *
     * @param {String} url                      Request URL.
     * @param {String|Object} [data]            Request data as query string or JSON object.
     * @param {Function} [callback]             The callback function will be executed after the request is completed.
     *                                          It gets the XMLHttpRequest object as the first argument.
     *
     * @example
     * // Using JSON data with Promise.
     * o_util.ajax.post("/url", {
     *     "lorem": "ipsum",
     *     "name": "binny"
     * }).then(function(xhr) {
     *     console.log("status", xhr.status);
     * });
     *
     * // Using query string and callback version.
     * o_util.ajax.post("/url", "lorem=ipsum&name=binny").then(function(xhr) {
     *     console.log("status", xhr.status);
     * });
     *
     * @return {Promise|XMLHttpRequest}     Returns an XMLHttpRequest object if called with callback.
     *                                      (https://developer.mozilla.org/de/docs/Web/API/XMLHttpRequest)
     *                                      Otherwise it returns a Promise with the XMLHttpRequest object as first argument.
     */
    function post(url, data, callback) {
        var contentType = "";

        if (typeof data === "object") {
            data = JSON.stringify(data);
            contentType = "application/json; charset=utf-8";
        }

        return o_util.ajax({
            "method": "POST",
            "url": url,
            "data": data,
            "contentType": contentType || "application/x-www-form-urlencoded; charset=utf-8"
        }, callback);
    }

    /**
     * Abort a running XMLHttpRequest.
     * IMPORTANT: It's necessary to use the callback version to get the native XHR object and save it to a variable!
     *
     * @param {XMLHttpRequest} xhr      XMLHttpRequest to abort.
     *
     * @example
     * // Save the retrieved XHR object to a variable.
     * var xhr = o_util.ajax.get("/url", function(xhr) {
     *     // ...
     * });
     *
     * // Use this variable to abort the running request.
     * o_util.ajax.abort(xhr);
     */
    function abort(xhr) {
        xhr.abort();
    }

    /**
     * Return the public API.
     */
    return {
        "get": get,
        "getJSON": getJSON,
        "getScript": getScript,
        "post": post,
        /* TEST-CODE */
        "_getCustomTimeStamp": _getCustomTimeStamp,
        /* END TEST-CODE */
        "abort": abort,
        "active": active
    };
})(), false, false);

o_global.helper = o_global.helper || {};

/**
 * Deprecated AJAX caller.
 *
 * @deprecated Use o_util.ajax() or o_util.ajax.get() instead.
 *
 * @param {String} url                  Url to be fetched.
 * @param {Function} contentCallback    Function which is passed result.
 * @param {Function} finishCallback     Function which is executed after AJAX request has finished.
 */
o_global.helper.ajax = function(url, contentCallback, finishCallback) {
    "use strict";

    o_global.debug.warn("o_global.helper.ajax is deprecated, use o_util.ajax or o_util.ajax.get instead.");

    o_util.ajax.get(url).then(function(xhr) {
        if (typeof contentCallback === "function") {
            if (xhr.status === 200) {
                contentCallback(xhr.responseText, xhr.status);
            } else {
                contentCallback("<div class=\"p_message p_message--error p_layer--error\"><b>Entschuldigung!</b> Aufgrund von technischen Problemen kann der Inhalt leider nicht geladen werden. Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.</div>", xhr.status);
            }
        }

        if (typeof finishCallback === "function") {
            finishCallback(xhr.status);
        }
    });
};
